import {
  getSharedPreferences,
  saveSharedPreferences,
  SHARELIST
} from "../../users/data/preferences.js"
export { SHARELIST }

const DISTRIBUTION_LIST_PROPERTIES = new Set(['id', 'name', 'emailList'])
const getDistributionListsFileName = (group = SHARELIST) => `${group}_distribution_lists.json`

const normalizeDistributionListEmails = (emailList) => {
  if (!Array.isArray(emailList)) return []
  return emailList
    .map((email) => String(email ?? '').trim())
    .filter(Boolean)
}

const createDistributionListId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const readDistributionListPropertyMap = (distributionList) => {
  const values = Object.create(null)

  if (Array.isArray(distributionList?.properties)) {
    distributionList.properties.forEach((propertyValue) => {
      const property = propertyValue?.property
      if (DISTRIBUTION_LIST_PROPERTIES.has(property)) {
        values[property] = propertyValue?.value
      }
    })
  } else {
    DISTRIBUTION_LIST_PROPERTIES.forEach((property) => {
      if (Object.prototype.hasOwnProperty.call(distributionList ?? {}, property)) {
        values[property] = distributionList[property]
      }
    })
  }

  const name = String(values.name ?? '').trim()
  if (!name) return undefined

  return {
    id: String(values.id ?? createDistributionListId()),
    name,
    emailList: normalizeDistributionListEmails(values.emailList)
  }
}

const createDistributionListPropertyMap = (distributionList) => ({
  properties: [
    { property: 'id', value: distributionList.id },
    { property: 'name', value: distributionList.name },
    { property: 'emailList', value: distributionList.emailList }
  ]
})

const normalizeDistributionLists = (preferences) => {
  const source = Array.isArray(preferences)
    ? preferences
    : Array.isArray(preferences?.distributionLists)
      ? preferences.distributionLists
      : []

  return source
    .map(readDistributionListPropertyMap)
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name))
}

const serializeDistributionLists = (distributionLists) =>
  distributionLists.map(createDistributionListPropertyMap)

export const getDistributionLists = async (group = SHARELIST) => {
  const fileName = getDistributionListsFileName(group)
  const preferences = await getSharedPreferences(fileName)

  if (!preferences) {
    await saveSharedPreferences(fileName, [])
    return []
  }

  return normalizeDistributionLists(preferences)
}

export const saveDistributionList = async (group = SHARELIST, distributionList) => {
  const name = String(distributionList?.name ?? '').trim()
  if (!name) {
    throw new Error('Distribution List name is required')
  }

  const emailList = normalizeDistributionListEmails(distributionList?.emailList)
  const distributionLists = await getDistributionLists(group)
  const id = distributionList?.id ? String(distributionList.id) : undefined
  const normalizedName = name.toLowerCase()
  const existingIndex = distributionLists.findIndex((list) =>
    id ? list.id === id : list.name.toLowerCase() === normalizedName
  )
  const duplicateNameIndex = distributionLists.findIndex((list) =>
    list.name.toLowerCase() === normalizedName && (!id || list.id !== id)
  )

  if (duplicateNameIndex >= 0) {
    throw new Error('A Distribution List with that name already exists')
  }

  const savedDistributionList = {
    id: id ?? createDistributionListId(),
    name,
    emailList
  }

  const nextDistributionLists = existingIndex >= 0
    ? distributionLists.map((list, index) => index === existingIndex ? savedDistributionList : list)
    : [...distributionLists, savedDistributionList]

  const sortedDistributionLists = nextDistributionLists.sort((a, b) => a.name.localeCompare(b.name))

  await saveSharedPreferences(
    getDistributionListsFileName(group),
    serializeDistributionLists(sortedDistributionLists)
  )

  return sortedDistributionLists
}

export const deleteDistributionList = async (group = SHARELIST, distributionList) => {
  const id = distributionList?.id ? String(distributionList.id) : undefined
  const name = String(distributionList?.name ?? '').trim().toLowerCase()
  if (!id && !name) {
    throw new Error('Distribution List id or name is required')
  }

  const distributionLists = await getDistributionLists(group)
  const nextDistributionLists = distributionLists.filter((list) =>
    id ? list.id !== id : list.name.toLowerCase() !== name
  )

  await saveSharedPreferences(
    getDistributionListsFileName(group),
    serializeDistributionLists(nextDistributionLists)
  )

  return nextDistributionLists
}
